import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ArrowLeft, XCircle, AlertTriangle, Banknote, CreditCard } from 'lucide-react';
import { ExpenseReimbursement } from '@/types/expense';
import { Employee } from '@/types/employee';
import { Invoice } from '@/types/payments';
import { EXPENSE_CATEGORY_INFO } from '@/constants/expenseCategories';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { toast } from 'sonner';
import ConfirmPaymentModal from '@/components/payments/confirmation/ConfirmPaymentModal';
import MarkPaidExternallyModal from './MarkPaidExternallyModal';
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import SekEquivalent from "@/components/shared/SekEquivalent";
import { formatCurrency } from '@/utils/paymentUtils';
import { expenseToPaymentInvoice, useExpenseStatus } from './expensePaymentInvoice';
import { useReceiptLabels } from '@/components/receipts/useReceiptLabels';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { formatAmount } from "@financialharmony/ui";

interface ExpenseReviewScreenProps {
  expenseId: string;
  onBack: () => void;
}

const ExpenseReviewScreen: React.FC<ExpenseReviewScreenProps> = ({ expenseId, onBack }) => {
  const t = useTranslations('consultantExpenses');
  const participants = useTranslations('consultantDraft.participants');
  const expenseStatus = useExpenseStatus();
  const receiptLabels = useReceiptLabels();
  const [expense, setExpense] = useState<ExpenseReimbursement | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoices, setPaymentInvoices] = useState<Invoice[]>([]);
  const [ibanDialogOpen, setIbanDialogOpen] = useState(false);
  const [ibanInput, setIbanInput] = useState('');
  const [ibanSaving, setIbanSaving] = useState(false);
  const [markPaidModalOpen, setMarkPaidModalOpen] = useState(false);

  const loadExpense = async () => {
    try {
      setLoading(true);
      const data = await ServiceRegistry.getExpenseService().getExpense(expenseId);
      setExpense(data);
    } catch (error) {
      toast.error(t('review.toasts.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await ServiceRegistry.getEmployeeService().listEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to load employees', error);
    }
  };

  useEffect(() => {
    loadExpense();
    loadEmployees();
  }, [expenseId]);

  const handleEmployeeChange = async (employeeId: string) => {
    if (!expense) return;
    try {
      setEmployeeLoading(true);
      const updated = await ServiceRegistry.getExpenseService().updateEmployee(expense.id, employeeId);
      setExpense(updated);
      toast.success(t('review.toasts.employeeUpdated'));
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setEmployeeLoading(false);
    }
  };

  const openPaymentModal = (exp: ExpenseReimbursement, payableId: string, paymentDate?: string, sourceAccountId?: string) => {
    const invoiceForModal: Invoice = expenseToPaymentInvoice(exp, {
      payableId,
      paymentDate,
      sourceAccountId,
      fallbackVendor: t('employeeFallback'),
    });
    setPaymentInvoices([invoiceForModal]);
    setPaymentModalOpen(true);
  };

  const handleApprovePayment = async () => {
    if (!expense) return;
    try {
      setActionLoading(true);
      const { expense: updated, payable } = await ServiceRegistry.getExpenseService().approveAndQueuePayment(expense.id);
      setExpense(updated);
      openPaymentModal(updated, payable.id, payable.payment_date, payable.source_account_id ?? undefined);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaidExternallyComplete = () => {
    setMarkPaidModalOpen(false);
    loadExpense();
  };

  const handleReject = async () => {
    if (!expense || !rejectionReason.trim()) return;
    try {
      setActionLoading(true);
      await ServiceRegistry.getExpenseService().rejectExpense(expense.id, rejectionReason);
      toast.success(t('review.toasts.rejected'));
      loadExpense();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveIban = async () => {
    if (!expense?.employee_id) return;
    const cleaned = ibanInput.replace(/\s/g, '').toUpperCase();
    if (cleaned.length < 15 || cleaned.length > 34 || !/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleaned)) {
      toast.error(t('review.toasts.invalidIban'));
      return;
    }
    try {
      setIbanSaving(true);
      await ServiceRegistry.getEmployeeService().updateEmployee(expense.employee_id, { iban: cleaned });
      toast.success(t('review.toasts.ibanSaved'));
      setIbanDialogOpen(false);
      await loadExpense();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setIbanSaving(false);
    }
  };

  if (loading || !expense) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const statusConfig = expenseStatus(expense.status);
  const canApprovePayment = expense.status === 'awaiting_payment_approval';
  const canPayExisting = expense.status === 'payment_queued' && expense.payable_id;
  const missingIban = (canApprovePayment || canPayExisting) && (!expense.employee_id || !expense.employee_iban);
  const canReject = ['pending_review', 'suggestion_ready', 'awaiting_payment_approval'].includes(expense.status);
  const canEditEmployee = ['draft', 'pending_review', 'suggestion_ready', 'awaiting_payment_approval'].includes(expense.status);
  const isExtracting = expense.status === 'draft';

  const detailContent = (
    <>
      {/* Extracted Data */}
      <Card>
        <CardHeader>
          <CardTitle>{t('review.detailsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isExtracting ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('review.extracting')}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('review.labels.vendor')}</Label>
                <p className="font-medium">{expense.vendor || '—'}</p>
              </div>
              <div>
                <Label>{t('review.labels.amount')}</Label>
                <p className="font-medium">
                  {expense.amount ? formatCurrency(expense.amount, expense.currency || 'SEK') : '—'}
                </p>
                <SekEquivalent sekAmount={expense.sek_amount} currency={expense.currency || 'SEK'} />
              </div>
              <div>
                <Label>{t('review.labels.date')}</Label>
                <p className="font-medium">{expense.expense_date || '—'}</p>
              </div>
              <div>
                <Label>{t('review.labels.category')}</Label>
                <p className="font-medium">{expense.expense_category ? (EXPENSE_CATEGORY_INFO[expense.expense_category as keyof typeof EXPENSE_CATEGORY_INFO]?.label || expense.expense_category) : '—'}</p>
              </div>
              {expense.remittance_information && (
                <div className="col-span-2">
                  <Label>{t('review.labels.reference')}</Label>
                  <p className="font-medium">{expense.remittance_information}</p>
                </div>
              )}
              {expense.payment_method && (
                <div>
                  <Label>{t('review.labels.paymentMethod')}</Label>
                  <p className="font-medium capitalize">{receiptLabels.paymentMethod(expense.payment_method)}</p>
                </div>
              )}
              {expense.vat_amount !== null && expense.vat_amount !== undefined && (
                <div>
                  <Label>{t('review.labels.vat')}</Label>
                  <p className="font-medium">
                    {formatAmount(Number(expense.vat_amount))} {expense.currency}
                    {expense.vat_deductible_percent < 100 && t('review.vatDeductible', { percent: String(expense.vat_deductible_percent) })}
                  </p>
                </div>
              )}
              <div>
                <Label>{t('review.labels.employee')}</Label>
                {canEditEmployee ? (
                  <Select
                    value={expense.employee_id || ''}
                    onValueChange={handleEmployeeChange}
                    disabled={employeeLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('review.selectEmployee')} />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.display_name}
                          {emp.iban ? ' \u2713' : t('review.noIban')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium">{expense.employee_name}</p>
                )}
              </div>
              <div>
                <Label>{t('review.labels.receipt')}</Label>
                <p className="font-medium">{expense.receipt_file_name}</p>
              </div>
              {expense.category_metadata?.participants && expense.category_metadata.participants.length > 0 && (
                <div className="col-span-2">
                  <Label>{participants('heading')}</Label>
                  <ul className="mt-1 text-sm list-disc list-inside space-y-0.5">
                    {expense.category_metadata.participants.map((p, i) => (
                      <li key={i}>
                        <span className="font-medium">{p.name}</span>
                        {p.company && <span className="text-muted-foreground"> — {p.company}</span>}
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({participants(p.source === 'employee' ? 'internal' : 'externalTag')})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {expense.extraction_status === 'failed' && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {t('review.extractionFailed')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bookkeeping Suggestion Section */}
      {expense.status === 'suggestion_ready' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('review.suggestionTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm">{t('review.suggestionBody')}</p>
          </CardContent>
        </Card>
      )}

      {expense.status === 'pending_review' && !isExtracting && (
        <Card>
          <CardHeader>
            <CardTitle>{t('review.bookkeepingTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('review.generating')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rejection reason display */}
      {expense.status === 'rejected' && expense.rejection_reason && (
        <Card>
          <CardContent className="pt-6">
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive">
              <strong>{t('review.rejectedLabel')}</strong> {expense.rejection_reason}
            </div>
          </CardContent>
        </Card>
      )}

      {/* IBAN warning */}
      {missingIban && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>{t('review.ibanWarning')}</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-yellow-400 text-yellow-800 hover:bg-yellow-100"
            onClick={() => { setIbanInput(''); setIbanDialogOpen(true); }}
          >
            {t('review.addIban')}
          </Button>
        </div>
      )}

      <Dialog open={ibanDialogOpen} onOpenChange={setIbanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('review.ibanDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="inline-iban">{t('review.ibanDialog.label')}</Label>
            <Input
              id="inline-iban"
              value={ibanInput}
              onChange={(e) => setIbanInput(e.target.value)}
              placeholder={t('review.ibanDialog.placeholder')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIbanDialogOpen(false)}>{t('review.ibanDialog.cancel')}</Button>
            <Button onClick={handleSaveIban} disabled={ibanSaving}>
              {ibanSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('review.ibanDialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View in Account Payables link */}
      {expense.payable_id && ['payment_queued', 'payment_initiated', 'paid', 'closed'].includes(expense.status) && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          <a href="/account-payables" className="underline font-medium">{t('review.viewPayables')}</a>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {canApprovePayment && (
          <>
            <Button onClick={handleApprovePayment} disabled={actionLoading || missingIban || !expense.employee_id}>
              {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              {t('actions.approvePay')}
            </Button>
            <Button variant="outline" onClick={() => setMarkPaidModalOpen(true)} disabled={actionLoading || missingIban || !expense.employee_id}>
              <Banknote className="mr-2 h-4 w-4" />
              {t('actions.approvePayExternally')}
            </Button>
          </>
        )}

        {canPayExisting && (
          <>
            <Button onClick={() => openPaymentModal(expense, expense.payable_id!)} disabled={actionLoading}>
              <CreditCard className="mr-2 h-4 w-4" />
              {t('actions.pay')}
            </Button>
            <Button variant="outline" onClick={() => setMarkPaidModalOpen(true)} disabled={actionLoading}>
              <Banknote className="mr-2 h-4 w-4" />
              {t('actions.markPaidExternally')}
            </Button>
          </>
        )}

        {canReject && !showRejectForm && (
          <Button variant="destructive" onClick={() => setShowRejectForm(true)} disabled={actionLoading}>
            <XCircle className="mr-2 h-4 w-4" />
            {t('review.reject')}
          </Button>
        )}

        {showRejectForm && (
          <div className="flex-1 space-y-2">
            <Textarea
              placeholder={t('review.rejectionPlaceholder')}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex gap-2">
              <Button variant="destructive" onClick={handleReject} disabled={!rejectionReason.trim() || actionLoading}>
                {t('review.confirmRejection')}
              </Button>
              <Button variant="outline" onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}>
                {t('review.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="space-y-4">
      {/* back button + badge row */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('review.back')}
        </Button>
        <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
      </div>

      {expense.receipt_file_path ? (
        <ResizablePanelGroup direction="horizontal" className="min-h-[600px]">
          <ResizablePanel defaultSize={45} minSize={25}>
            <DocumentViewerPanel
              filePath={expense.receipt_file_path}
              fileName={expense.receipt_file_name ?? undefined}
            />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={55} minSize={30} className="pl-4">
            <div className="h-full overflow-y-auto space-y-6 pb-4">
              {detailContent}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="space-y-6">
          {detailContent}
        </div>
      )}

      <ConfirmPaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        selectedInvoices={paymentInvoices}
        actionInvoiceId={null}
        onConfirmPayment={() => { loadExpense(); }}
        refreshData={async () => { await loadExpense(); }}
        autoStartSigning
      />

      <MarkPaidExternallyModal
        open={markPaidModalOpen}
        onOpenChange={setMarkPaidModalOpen}
        expense={expense}
        onComplete={handleMarkPaidExternallyComplete}
      />
    </div>
  );
};

export default ExpenseReviewScreen;
